/**
 * 발사체 연속 충돌 검사(continuous collision / sweep) — 순수 결정 로직.
 *
 * 왜 필요한가: 발사체 이동은 Phaser Arcade가 delta로 적분(`pos += v×dt`)하고, 충돌은
 * `physics.add.overlap`이 "현재 프레임 위치 1점"만 본다. 발사체가 빠르거나(speedMultiplier 누적)
 * 프레임이 드랍(delta spike)되면 한 프레임 이동량이 벽돌 두께를 넘어 overlap이 벽돌을 건너뛴다
 * (터널링 → 벽돌이 영원히 안 깨짐). 이전→현재 위치를 잇는 선분이 벽돌 AABB를 관통하면 hit으로 보아
 * overlap의 공백을 메운다. Arcade 적분은 그대로 두고 "검출"만 보완 → bounce/homing/pierce와 직교.
 */

/** 축정렬 사각형(중앙 origin) — 벽돌 충돌 박스. */
export interface Aabb {
  /** 박스 중앙 x 좌표 (Phaser world-space). */
  readonly cx: number;
  /** 박스 중앙 y 좌표 (Phaser world-space). */
  readonly cy: number;
  /** 가로 반폭 (width / 2). */
  readonly halfW: number;
  /** 세로 반폭 (height / 2). */
  readonly halfH: number;
}

/** 반지름 radius 원이 (from)→(to)로 이동한 선분. */
export interface SweepCircle {
  /** 이전 프레임 위치 x. */
  readonly fromX: number;
  /** 이전 프레임 위치 y. */
  readonly fromY: number;
  /** 현재 프레임 위치 x. */
  readonly toX: number;
  /** 현재 프레임 위치 y. */
  readonly toY: number;
  /** 발사체 충돌 반지름. */
  readonly radius: number;
}

/**
 * 반지름 r 원의 from→to 이동 선분이 AABB와 교차하는가.
 * AABB를 r만큼 팽창(Minkowski sum 근사 — 코너에서 보수적 과검출이나 누락<과검출이라 안전)한 뒤
 * Liang–Barsky 선분 클리핑으로 판정. 시작점이 팽창 박스 안이면 즉시 true(저속/정지 발사체 보존).
 */
export function sweepHitsAabb(seg: SweepCircle, box: Aabb): boolean {
  const minX = box.cx - box.halfW - seg.radius;
  const maxX = box.cx + box.halfW + seg.radius;
  const minY = box.cy - box.halfH - seg.radius;
  const maxY = box.cy + box.halfH + seg.radius;

  const dx = seg.toX - seg.fromX;
  const dy = seg.toY - seg.fromY;

  // Liang–Barsky: 매개변수 t∈[0,1] 구간을 4개 경계(slab)로 클립. 살아남는 구간이 있으면 교차.
  let t0 = 0;
  let t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0; // 경계와 평행 — 박스 밖이면 즉시 탈락(false)
    const r = q / p;
    if (p < 0) {
      if (r > t1) return false;
      if (r > t0) t0 = r;
    } else {
      if (r < t0) return false;
      if (r < t1) t1 = r;
    }
    return true;
  };

  return (
    clip(-dx, seg.fromX - minX) &&
    clip(dx, maxX - seg.fromX) &&
    clip(-dy, seg.fromY - minY) &&
    clip(dy, maxY - seg.fromY) &&
    t0 <= t1
  );
}

/**
 * 한 프레임 이동량이 임계를 초과해 overlap(이산 1점 샘플)만으로 부족한가.
 * 임계 이하(저속)는 overlap 빠른 경로에 위임, 초과(고속·delta spike)일 때만 sweep 보완을 켠다.
 */
export function needsSweep(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  threshold: number,
): boolean {
  return Math.hypot(toX - fromX, toY - fromY) > threshold;
}

/** 발사 속도 상한 — speedMultiplier(유물/모듈) 누적이 극단으로 커질 때 수치 안정 상한(sweep과 별개 방어 레이어). */
export function clampProjectileSpeed(speed: number, max: number): number {
  return Math.min(speed, max);
}
